"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.removeMarkers = exports.addMarkers = void 0;
const maplibre_gl_csp_js_1 = __importDefault(require("maplibre-gl/dist/maplibre-gl-csp.js"));
const makeAddress = (location) => {
    const { street1, street2, suite, city = '', state = '', zip = '' } = location;
    const streetAddress = [street1, street2, suite].filter(Boolean).join(', ');
    const stateAndZip = [state, zip].filter(Boolean).filter(Boolean).join(' ');
    return [streetAddress, city, stateAndZip].filter(Boolean).join(', ');
};
const validateSvgContent = (svgString) => {
    if (!svgString || typeof svgString !== 'string')
        return false;
    const maliciousPatterns = [
        /<script/i,
        /javascript:/i,
        /on\w+\s*=/i,
        /<iframe/i,
        /<object/i,
        /<embed/i,
        /<foreignObject/i,
        /xlink:href\s*=\s*["']javascript:/i
    ];
    const hasMaliciousContent = (content) => maliciousPatterns.some((pattern) => pattern.test(content));
    if (svgString.startsWith('data:image/svg+xml')) {
        try {
            const decoded = decodeURIComponent(svgString.split(',')[1] || '');
            return decoded.includes('<svg') && !hasMaliciousContent(decoded);
        }
        catch (_a) {
            return false;
        }
    }
    if (svgString.trim().startsWith('<svg') &&
        svgString.trim().endsWith('</svg>')) {
        return !hasMaliciousContent(svgString);
    }
    return false;
};
const createMarkerIcon = (svgString, location) => {
    const markerElem = document.createElement('img');
    markerElem.src = svgString;
    markerElem.style.cursor = 'pointer';
    const address = makeAddress(location);
    markerElem.alt = `${location.name}. ${address}`;
    markerElem.setAttribute('tabIndex', '0');
    markerElem.setAttribute('aria-label', `${location.name}. ${address}`);
    return markerElem;
};
const createCustomMarkerElem = (content, location) => {
    const markerElem = document.createElement('span');
    markerElem.classList.add('marker');
    const address = makeAddress(location);
    markerElem.setAttribute('aria-label', `${content}. ${location.name}. ${address}`);
    const markerContentElem = document.createElement('b');
    markerContentElem.appendChild(document.createTextNode(content));
    markerElem.appendChild(markerContentElem);
    return markerElem;
};
/**
 * Adds markers to the AWS Map instance with support for custom SVG icons.
 *
 * @param params - Configuration object for adding markers
 * @param params.locations - Array of location objects to create markers for
 * @param params.awsMapInstance - MapLibre GL JS map instance
 * @param params.isStaticMap - Whether the map is static (affects popover behavior)
 * @param params.defaultMarkerIcon - Optional global SVG content for all markers
 * @returns Array of marker references with metadata for external manipulation
 */
const addMarkers = ({ locations, awsMapInstance, isStaticMap, onMarkerClick, defaultMarkerIcon }) => {
    return locations
        .map((location, index) => {
        const { id, coordinates, popover, isSelected, isDisabled } = location;
        if (awsMapInstance && coordinates && id) {
            let markerElem;
            if (defaultMarkerIcon && validateSvgContent(defaultMarkerIcon)) {
                markerElem = createMarkerIcon(defaultMarkerIcon, location);
            }
            else {
                markerElem = createCustomMarkerElem((index + 1).toString(), location);
            }
            const marker = new maplibre_gl_csp_js_1.default.Marker({
                element: markerElem,
                opacity: isDisabled ? ' 0.5' : '1.0'
            })
                .setLngLat(coordinates)
                .addTo(awsMapInstance);
            const meta = {
                location,
                isSelected: Boolean(isSelected),
                isDisabled: Boolean(isDisabled)
            };
            if (isSelected) {
                marker.addClassName('selected');
            }
            if (isDisabled) {
                // not used for styling currently, but is used by tests
                marker.addClassName('disabled');
            }
            if (!isStaticMap && popover) {
                const popup = new maplibre_gl_csp_js_1.default.Popup({ offset: 25 }).setHTML(popover);
                marker.setPopup(popup);
            }
            // Add click event handler if provided
            if (!isStaticMap && onMarkerClick) {
                markerElem.addEventListener('click', (event) => {
                    event.stopPropagation();
                    onMarkerClick({
                        marker,
                        meta
                    });
                });
                // Make marker element clickable
                markerElem.style.cursor = 'pointer';
                markerElem.setAttribute('role', 'button');
                markerElem.setAttribute('tabindex', '0');
                // Add keyboard support
                markerElem.addEventListener('keydown', (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                        onMarkerClick({
                            marker,
                            meta
                        });
                    }
                });
            }
            return { marker, meta };
        }
        return null;
    })
        .filter(Boolean);
};
exports.addMarkers = addMarkers;
const removeMarkers = (markers) => {
    markers.forEach(({ marker }) => marker.remove());
};
exports.removeMarkers = removeMarkers;
//# sourceMappingURL=utils.js.map